import { PermissionManagerProvider } from '@efex-services/auth/role-permission';
import '@efexteam/design-tokens/build/css/_variables.css';
import theme from '@efexteam/mui-theme';
import CssBaseline from '@mui/material/CssBaseline';
import { ThemeProvider } from '@mui/material/styles';
import { LocalizationProvider } from '@mui/x-date-pickers';
import { AdapterLuxon } from '@mui/x-date-pickers/AdapterLuxon';
import SavingsCalculator from '@pages/SavingsCalculator/SavingsCalculator';
import ApproveBeneficiary from '@pages/approveBeneficiary';
import PaymentLinkMfe from '@pages/ecosystem/PaymentLink/PaymentLink';
import InactiveUser from '@pages/inactiveUser/InactiveUser';
import Exporter from '@pages/lending/components/Exporter/Exporter';
import MyAccounts from '@pages/myAccounts/MyAccounts';
import Onboarding from '@pages/onboarding';
import RequestTerms from '@pages/requestTerms/RequestTerms';
import Security from '@pages/security';
import EditTeamMember from '@pages/teamMember/editTeamMember';
import TransactionalProfile from '@pages/transactionalProfile/TransactionalProfile';
import ValidateAccount from '@pages/validateAccount/ValidateAccount';
import { Provider } from 'react-redux';
import { BrowserRouter, Route, Routes } from 'react-router-dom';
import { IntercomProvider } from 'react-use-intercom';
import AppInitializer from './AppInitializer';
import NavigateSetup from './components/NavigateSetup';
import LegacyAuthRoute from './components/protectedRoutes/LegacyAuthRoute';
import ActiveUserEnterprise from './layout/ActiveUserEnterprise';
import MainLayout from './layout/MainLayout';
import LoginLayout from './layout/login/LoginLayout';
import PasswordLayout from './layout/password/PasswordLayout';
import RegisterLayout from './layout/register/RegisterLayout';
import ResetLayout from './layout/reset/ResetLayout';
import WsCurrency from './modules/dashboard/currency/WsCurrency';
import ReceivableInitializer from './modules/receivables/ReceivableInitializer';
import Blank from './pages/Blank';
import Clients from './pages/clients/Clients';
import Credentials from './pages/credentials/Credentials';
import Dashboard from './pages/dashboard/Dashboard';
import ManualPaymentButton from './pages/manualPaymentButton/ManualPaymentButton';
import Payables from './pages/payables/Payables';
import Profile from './pages/profile';
import AddTeamMember from './pages/teamMember/addTeamMember';
import TransferDetails from './pages/transferDetails/TransferDetails';
import DisableRevisionLayout from './pages/under-revision/DisableRevisionLayout';
import UnderRevisionLayout from './pages/under-revision/UnderRevisionLayout';
import './scss/App.scss';
import { store } from './store';

const App = () => {
  const INTERCOM_APP_ID = import.meta.env.VITE_INTERCOMM_APP_ID;

  return (
    <ThemeProvider theme={theme}>
      <Provider store={store()}>
        <LocalizationProvider dateAdapter={AdapterLuxon}>
          <IntercomProvider appId={INTERCOM_APP_ID}>
            <CssBaseline />
            <PermissionManagerProvider>
              <BrowserRouter>
                <AppInitializer>
                  <NavigateSetup />
                  <Routes>
                    <Route path="login" element={<LoginLayout />} />
                    <Route
                      path="payment-link"
                      element={<ManualPaymentButton />}
                    />
                    <Route path="reset" element={<ResetLayout />} />
                    <Route path="password" element={<PasswordLayout />} />
                    <Route path="register" element={<RegisterLayout />} />
                    <Route
                      path="register-sub-account"
                      element={<RegisterLayout />}
                    />
                    <Route
                      path="active-account"
                      element={<ActiveUserEnterprise />}
                    />
                    <Route
                      path="under-revision"
                      element={<UnderRevisionLayout />}
                    />
                    <Route
                      path="account-review"
                      element={<DisableRevisionLayout />}
                    />
                    <Route path="lending" element={<Exporter />} />
                    <Route path="onboarding" element={<Onboarding />} />
                    <Route
                      path="accept-beneficiary"
                      element={<ApproveBeneficiary />}
                    />
                    <Route path="/" element={<MainLayout />}>
                      <Route
                        path="dashboard"
                        element={
                          <WsCurrency>
                            <Dashboard />
                          </WsCurrency>
                        }
                      />
                      <Route
                        path="receivables"
                        element={<ReceivableInitializer />}
                      />
                      <Route path="payables" element={<Payables />} />
                      <Route path="balances" element={<MyAccounts />} />
                      <Route path="settings" element={<Blank />} />
                      <Route path="clients" element={<Clients />} />
                      <Route path="profile" element={<Profile />} />
                      <Route path="developers" element={<Credentials />} />
                      <Route
                        path="security"
                        element={
                          <LegacyAuthRoute>
                            <Security />
                          </LegacyAuthRoute>
                        }
                      />
                      <Route path="savings" element={<SavingsCalculator />} />
                      <Route
                        path="validate-account"
                        element={<ValidateAccount />}
                      />
                      <Route path="inactive" element={<InactiveUser />} />
                      <Route
                        path="terms-and-conditions"
                        element={<RequestTerms />}
                      />
                      <Route
                        path="transactional-profile"
                        element={<TransactionalProfile />}
                      />
                      <Route
                        path="create-payment-link"
                        element={<PaymentLinkMfe />}
                      />
                      <Route
                        path="edit-team-member/:id"
                        element={<EditTeamMember />}
                      />
                      <Route
                        path="add-team-member"
                        element={<AddTeamMember />}
                      />
                      <Route
                        path="transfer-details/:uuid"
                        element={<TransferDetails />}
                      />
                      <Route path="*" element={<LoginLayout />} />
                    </Route>
                  </Routes>
                </AppInitializer>
              </BrowserRouter>
            </PermissionManagerProvider>
          </IntercomProvider>
        </LocalizationProvider>
      </Provider>
    </ThemeProvider>
  );
};

export default App;
